import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchAllVisitTypes,
  fetchAllVisitStages,
  createVisitSchemeStageDocuments,
  fetchAllDocumentTypes,
} from "../apis/patientService";
import { fetchCaseTypes } from "../apis/caseService";
import { wizardSteps } from "../props/SchemeOnboardingProps";
import {
  ISchemeOnboarding,
  schemeOnboardingPayload,
  VisitSchemeStageDocuments,
  wizard,
} from "../interfaces/ISchemeOnboarding";
import { fetchAllPortals } from "../apis/healthCareService/getHealthCareService";
import { postSchemeOnboarding } from "../apis/schemeService";

const initialState: ISchemeOnboarding = {
  allWizardSteps: wizardSteps,
  currentStep: 0,
  schemes: null,
  caseTypes: null,
  visitTypes: null,
  visitStages: null,
  portalMetaDeta: null,
  schemeOnboardForm: null,
  schemeOnboarded: null,
  documentTypesList: null,
  isLoading: false,
  error: null,
};

export const getAllVisitTypes = createAsyncThunk(
  "schemeOnboarding/getAllVisitTypes",
  async () => {
    const response = await fetchAllVisitTypes();
    return response;
  }
);

export const getAllVisitStages = createAsyncThunk(
  "schemeOnboarding/getAllVisitStages",
  async () => {
    const response = await fetchAllVisitStages();
    return response;
  }
);

export const getAllPortalMetaDeta = createAsyncThunk(
  "schemeOnboarding/getAllPortalMetaDeta",
  async () => {
    const response = await fetchAllPortals();
    return response;
  }
);

export const schemeOnboarding = createAsyncThunk(
  "schemeOnboarding/schemeOnboarding",
  async (payloadData: schemeOnboardingPayload) => {
    const response = await postSchemeOnboarding(payloadData);
    return response;
  }
);

export const getCaseTypes = createAsyncThunk(
  "schemeOnboarding/getCaseTypes",
  async ({
    accessToken,
    schemeId,
  }: {
    accessToken: string;
    schemeId: number;
  }) => {
    const response = await fetchCaseTypes(accessToken, schemeId);
    return response;
  }
);

export const postVisitSchemeStageDocuments = createAsyncThunk(
  "schemeOnboarding/createVisitSchemeStageDocuments",
  async (requestData: VisitSchemeStageDocuments) => {
    const response = await createVisitSchemeStageDocuments(requestData);
    return response;
  }
);

export const getAllDocumentTypes = createAsyncThunk(
  "schemeOnboarding/allDocumentTypes",
  async () => {
    const response = await fetchAllDocumentTypes();
    return response;
  }
);

const schemeOnboardingSlice = createSlice({
  name: "schemeOnboarding",
  initialState,
  reducers: {
    nextStep: (state) => {
      if (state.currentStep < wizardSteps.length - 1) {
        state.currentStep += 1;
        // state.currentStep = wizardSteps[state.currentStep+1]?.steps;
      }
    },
    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
        // state.currentStep = wizardSteps[state.currentStep-1]?.steps;
      }
    },
    setWizards: (state, action: PayloadAction<Array<wizard>>) => {
      state.allWizardSteps = action.payload;
    },
    setCanNavigate: (
      state,
      action: PayloadAction<{ step: number; canNavigate: boolean }>
    ) => {
      const { step, canNavigate } = action.payload;
      if (state.allWizardSteps[step]) {
        state.allWizardSteps[step].canNavigate = canNavigate;
      }
    },
    setInitialSate: (state) => {
      state.allWizardSteps = wizardSteps;
      state.currentStep = 0;
      state.schemes = null;
      state.caseTypes = null;
      state.visitTypes = null;
      state.visitStages = null;
      state.portalMetaDeta = null;
      state.schemeOnboardForm = null;
      state.schemeOnboarded = null;
      state.documentTypesList = null;
      state.isLoading = false;
      state.error = null;
    },
    resetStep: (state) => {
      state.currentStep = 0;
    },
    clearAllWizardSteps: (state) => {
      state.allWizardSteps = wizardSteps;
    },
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    setSchemeOnboarded(state, action) {
      state.schemeOnboarded = action.payload;
    },
    setSchemeOnboardForm(state, action) {
      state.schemeOnboardForm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllVisitTypes.pending, (state, action) => {
        state.visitTypes = null;
      })
      .addCase(getAllVisitTypes.fulfilled, (state, action) => {
        state.visitTypes = action.payload;
      })
      .addCase(getAllVisitTypes.rejected, (state, action) => {
        state.visitTypes = null;
      })
      .addCase(getAllVisitStages.pending, (state, action) => {
        state.visitStages = null;
      })
      .addCase(getAllVisitStages.fulfilled, (state, action) => {
        state.visitStages = action.payload;
      })
      .addCase(getAllVisitStages.rejected, (state, action) => {
        state.visitStages = null;
      })
      .addCase(getAllPortalMetaDeta.pending, (state, action) => {
        state.visitStages = null;
      })
      .addCase(getAllPortalMetaDeta.fulfilled, (state, action) => {
        state.portalMetaDeta = action.payload?.portals ?? null;
      })
      .addCase(getAllPortalMetaDeta.rejected, (state, action) => {
        state.visitStages = null;
      })
      .addCase(schemeOnboarding.pending, (state, action) => {
        state.isLoading = true;
        state.schemeOnboarded = null;
      })
      .addCase(schemeOnboarding.fulfilled, (state, action) => {
        state.isLoading = false;
        state.schemeOnboarded = action.payload ?? null;
        if (
          state.schemeOnboarded &&
          state.currentStep < wizardSteps.length - 1
        ) {
          state.currentStep += 1;
          // state.currentStep = wizardSteps[state.currentStep+1]?.steps;
        }
      })
      .addCase(schemeOnboarding.rejected, (state, action) => {
        state.isLoading = false;
        state.schemeOnboarded = null;
      })
      .addCase(getCaseTypes.pending, (state, action) => {
        state.caseTypes = null;
      })
      .addCase(getCaseTypes.fulfilled, (state, action) => {
        state.caseTypes = action.payload?.portalNames ?? null;
      })
      .addCase(getCaseTypes.rejected, (state, action) => {
        state.schemeOnboarded = null;
      })
      .addCase(getAllDocumentTypes.pending, (state, action) => {
        state.documentTypesList = null;
      })
      .addCase(getAllDocumentTypes.fulfilled, (state, action) => {
        state.documentTypesList = action.payload ?? null;
      })
      .addCase(getAllDocumentTypes.rejected, (state, action) => {
        state.documentTypesList = null;
      })
      .addCase(postVisitSchemeStageDocuments.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(postVisitSchemeStageDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(postVisitSchemeStageDocuments.rejected, (state, action) => {
        state.isLoading = false;
      });
  },
});

export const {
  nextStep,
  prevStep,
  setWizards,
  setCanNavigate,
  setCurrentStep,
  setSchemeOnboarded,
  setSchemeOnboardForm,
  setInitialSate,
} = schemeOnboardingSlice.actions;
export default schemeOnboardingSlice.reducer;
